import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  doc, 
  getDoc, 
  addDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { logActivity, ACTIVITY_TYPES } from '../utils/activityLogger';

/**
 * Smart Escalation Engine - Phase 2 Implementation
 * 
 * Features:
 * 1. Multi-factor escalation triggers
 * 2. Progressive escalation levels
 * 3. Smart notification routing
 * 4. Real-time escalation monitoring
 */
class SmartEscalationService {
  constructor(db) {
    this.db = db;
    this.activeEscalations = new Map();
    this.escalationListeners = new Map();
    this.isDisabled = false; // Add disabled state to prevent infinite loops
    this.permissionError = null;
  }

  /**
   * ESCALATION TRIGGER RULES
   */
  getEscalationRules() {
    return [
      {
        id: 'overdue_critical_tasks',
        name: 'Critical Task Overdue Escalation',
        description: 'Escalate when critical tasks are overdue',
        priority: 'high',
        triggers: [
          {
            condition: 'task_overdue',
            taskPriority: 'critical',
            overdueMinutes: 30,
            escalationLevel: 1,
            escalateTo: ['cmt_leader', 'incident_lead']
          },
          {
            condition: 'task_overdue',
            taskPriority: 'critical',
            overdueMinutes: 60,
            escalationLevel: 2,
            escalateTo: ['cmt_admin', 'cmt_leader', 'incident_lead']
          },
          {
            condition: 'task_overdue',
            taskPriority: 'critical',
            overdueMinutes: 120,
            escalationLevel: 3,
            escalateTo: ['all_cmt_members', 'external_contacts']
          }
        ]
      },
      {
        id: 'incident_severity_escalation',
        name: 'Incident Severity Escalation',
        description: 'Escalate based on incident severity and response time',
        priority: 'critical',
        triggers: [
          {
            condition: 'severity_response_time',
            severity: 'Extreme',
            maxResponseMinutes: 15,
            escalationLevel: 1,
            escalateTo: ['cmt_leader', 'safety_security']
          },
          {
            condition: 'severity_response_time',
            severity: 'High',
            maxResponseMinutes: 30,
            escalationLevel: 1,
            escalateTo: ['cmt_leader']
          },
          {
            condition: 'severity_response_time',
            severity: 'Moderate',
            maxResponseMinutes: 60,
            escalationLevel: 1,
            escalateTo: ['incident_lead']
          }
        ]
      },
      {
        id: 'workload_overload_escalation',
        name: 'Team Overload Escalation',
        description: 'Escalate when team members are overloaded',
        priority: 'medium',
        triggers: [
          {
            condition: 'excessive_workload',
            taskThreshold: 7,
            escalationLevel: 1,
            escalateTo: ['cmt_leader', 'incident_lead']
          },
          {
            condition: 'multiple_critical_conflicts',
            criticalTaskThreshold: 3,
            escalationLevel: 2,
            escalateTo: ['cmt_admin', 'external_resources']
          }
        ]
      },
      {
        id: 'communication_breakdown',
        name: 'Communication Breakdown Escalation',
        description: 'Escalate when communication patterns indicate issues',
        priority: 'high',
        triggers: [
          {
            condition: 'no_updates',
            timeWindowMinutes: 45,
            escalationLevel: 1,
            escalateTo: ['cmt_leader', 'comms_media']
          },
          {
            condition: 'unassigned_critical_tasks',
            taskCount: 3,
            escalationLevel: 1,
            escalateTo: ['cmt_leader', 'incident_lead']
          }
        ]
      },
      {
        id: 'external_pressure_escalation',
        name: 'External Pressure Escalation',
        description: 'Escalate based on external factors and time pressure',
        priority: 'high',
        triggers: [
          {
            condition: 'incident_duration',
            durationHours: 2,
            severity: ['High', 'Extreme'],
            escalationLevel: 1,
            escalateTo: ['cmt_admin', 'external_contacts']
          },
          {
            condition: 'media_attention',
            escalationLevel: 2,
            escalateTo: ['comms_media', 'cmt_admin', 'legal']
          }
        ]
      }
    ];
  }

  /**
   * Initialize escalation monitoring for an incident
   */
  async initializeEscalationMonitoring(incidentId, orgId) {
    try {
      console.log('🚨 Initializing smart escalation monitoring for incident:', incidentId);
      
      // Test permissions first by attempting a simple read
      const escalationsRef = collection(this.db, 'c3_escalations');
      const testQuery = query(escalationsRef, where('incidentId', '==', incidentId), limit(1));
      
      try {
        await getDocs(testQuery);
        console.log('✅ Escalation service has required permissions');
      } catch (permissionError) {
        console.warn('⚠️ Escalation service disabled due to insufficient permissions:', permissionError.message);
        this.isDisabled = true;
        this.permissionError = permissionError;
        return; // Exit early - don't set up any listeners
      }
      
      // Set up real-time monitoring only if permissions are OK
      await this.setupTaskEscalationListener(incidentId, orgId);
      await this.setupIncidentEscalationListener(incidentId, orgId);
      await this.setupPeriodicEscalationCheck(incidentId, orgId);
      
      console.log('✅ Escalation monitoring initialized successfully');
      
    } catch (error) {
      console.error('Error initializing escalation monitoring:', error);
      this.isDisabled = true;
      this.permissionError = error;
      throw error;
    }
  }

  /**
   * Set up task-based escalation monitoring
   */
  async setupTaskEscalationListener(incidentId, orgId) {
    const tasksRef = collection(this.db, 'c3_tasks');
    const q = query(
      tasksRef,
      where('incidentId', '==', incidentId),
      where('orgId', '==', orgId)
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      if (!snapshot.metadata.hasPendingWrites) {
        await this.evaluateTaskEscalations(incidentId, orgId, snapshot.docs);
      }
    });

    this.escalationListeners.set(`tasks_${incidentId}`, unsubscribe);
  }

  /**
   * Set up incident-based escalation monitoring
   */
  async setupIncidentEscalationListener(incidentId, orgId) {
    const incidentRef = doc(this.db, 'c3_incidents', incidentId);

    const unsubscribe = onSnapshot(incidentRef, async (snapshot) => {
      if (snapshot.exists() && !snapshot.metadata.hasPendingWrites) {
        await this.evaluateIncidentEscalations(incidentId, orgId, snapshot.data());
      }
    });

    this.escalationListeners.set(`incident_${incidentId}`, unsubscribe);
  }

  /**
   * Set up periodic escalation checks
   */
  async setupPeriodicEscalationCheck(incidentId, orgId) {
    // Check every 5 minutes for time-based escalations
    const interval = setInterval(async () => {
      await this.performPeriodicEscalationCheck(incidentId, orgId);
    }, 5 * 60 * 1000); // 5 minutes

    this.escalationListeners.set(`periodic_${incidentId}`, () => clearInterval(interval));
  }

  /**
   * Evaluate task-based escalation triggers
   */
  async evaluateTaskEscalations(incidentId, orgId, taskDocs) {
    try {
      const tasks = taskDocs.map(doc => ({ id: doc.id, ...doc.data() }));
      const now = new Date();
      const rules = this.getEscalationRules();

      // Check overdue critical tasks
      const overdueRule = rules.find(r => r.id === 'overdue_critical_tasks');
      if (overdueRule) {
        await this.checkOverdueTaskEscalations(incidentId, orgId, tasks, overdueRule, now);
      }

      // Check workload escalations
      const workloadRule = rules.find(r => r.id === 'workload_overload_escalation');
      if (workloadRule) {
        await this.checkWorkloadEscalations(incidentId, orgId, tasks, workloadRule);
      }

      // Check communication breakdown
      const commRule = rules.find(r => r.id === 'communication_breakdown');
      if (commRule) {
        await this.checkCommunicationEscalations(incidentId, orgId, tasks, commRule, now);
      }

    } catch (error) {
      console.error('Error evaluating task escalations:', error);
    }
  }

  /**
   * Check for overdue task escalations
   */
  async checkOverdueTaskEscalations(incidentId, orgId, tasks, rule, now) {
    for (const trigger of rule.triggers) {
      if (trigger.condition !== 'task_overdue') continue;

      const overdueTasks = tasks.filter(task => {
        if (task.status === 'complete' || task.priority !== trigger.taskPriority) return false;
        if (!task.dueDate) return false;

        const dueDate = task.dueDate.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
        const overdueMinutes = (now - dueDate) / (1000 * 60);

        return overdueMinutes >= trigger.overdueMinutes;
      });

      if (overdueTasks.length > 0) {
        await this.triggerEscalation({
          incidentId,
          orgId,
          ruleId: rule.id,
          triggerType: trigger.condition,
          escalationLevel: trigger.escalationLevel,
          escalateTo: trigger.escalateTo,
          reason: `${overdueTasks.length} critical tasks overdue by ${trigger.overdueMinutes}+ minutes`,
          affectedTasks: overdueTasks.map(t => ({ id: t.id, name: t.name, overdue: true })),
          severity: 'high',
          requiresImmediate: trigger.escalationLevel >= 2
        });
      }
    }
  }

  /**
   * Check for workload escalations
   */
  async checkWorkloadEscalations(incidentId, orgId, tasks, rule) {
    // Group tasks by owner
    const tasksByOwner = {};
    tasks.filter(t => t.status !== 'complete' && t.owner).forEach(task => {
      if (!tasksByOwner[task.owner]) tasksByOwner[task.owner] = [];
      tasksByOwner[task.owner].push(task);
    });

    for (const trigger of rule.triggers) {
      if (trigger.condition === 'excessive_workload') {
        // Check for users with too many tasks
        const overloadedUsers = Object.entries(tasksByOwner).filter(
          ([owner, userTasks]) => userTasks.length >= trigger.taskThreshold
        );

        if (overloadedUsers.length > 0) {
          await this.triggerEscalation({
            incidentId,
            orgId,
            ruleId: rule.id,
            triggerType: trigger.condition,
            escalationLevel: trigger.escalationLevel,
            escalateTo: trigger.escalateTo,
            reason: `${overloadedUsers.length} team members overloaded (${trigger.taskThreshold}+ tasks each)`,
            affectedUsers: overloadedUsers.map(([owner, userTasks]) => ({
              owner,
              taskCount: userTasks.length,
              tasks: userTasks.map(t => t.name)
            })),
            severity: 'medium',
            requiresImmediate: false
          });
        }
      }

      if (trigger.condition === 'multiple_critical_conflicts') {
        // Check for users with multiple critical tasks
        const criticalConflicts = Object.entries(tasksByOwner).filter(
          ([owner, userTasks]) => {
            const criticalTasks = userTasks.filter(t => t.priority === 'critical');
            return criticalTasks.length >= trigger.criticalTaskThreshold;
          }
        );

        if (criticalConflicts.length > 0) {
          await this.triggerEscalation({
            incidentId,
            orgId,
            ruleId: rule.id,
            triggerType: trigger.condition,
            escalationLevel: trigger.escalationLevel,
            escalateTo: trigger.escalateTo,
            reason: `${criticalConflicts.length} users with ${trigger.criticalTaskThreshold}+ critical tasks`,
            affectedUsers: criticalConflicts.map(([owner, userTasks]) => ({
              owner,
              criticalTasks: userTasks.filter(t => t.priority === 'critical').length
            })),
            severity: 'high',
            requiresImmediate: true
          });
        }
      }
    }
  }

  /**
   * Check for communication breakdown escalations
   */
  async checkCommunicationEscalations(incidentId, orgId, tasks, rule, now) {
    for (const trigger of rule.triggers) {
      if (trigger.condition === 'unassigned_critical_tasks') {
        const unassignedCritical = tasks.filter(t => 
          t.status !== 'complete' && 
          t.priority === 'critical' && 
          !t.owner
        );

        if (unassignedCritical.length >= trigger.taskCount) {
          await this.triggerEscalation({
            incidentId,
            orgId,
            ruleId: rule.id,
            triggerType: trigger.condition,
            escalationLevel: trigger.escalationLevel,
            escalateTo: trigger.escalateTo,
            reason: `${unassignedCritical.length} critical tasks remain unassigned`,
            affectedTasks: unassignedCritical.map(t => ({ id: t.id, name: t.name, unassigned: true })),
            severity: 'high',
            requiresImmediate: true
          });
        }
      }
    }
  }

  /**
   * Evaluate incident-based escalations
   */
  async evaluateIncidentEscalations(incidentId, orgId, incidentData) {
    try {
      const rules = this.getEscalationRules();
      const now = new Date();

      // Check severity-based escalations
      const severityRule = rules.find(r => r.id === 'incident_severity_escalation');
      if (severityRule) {
        await this.checkSeverityEscalations(incidentId, orgId, incidentData, severityRule, now);
      }

      // Check external pressure escalations
      const externalRule = rules.find(r => r.id === 'external_pressure_escalation');
      if (externalRule) {
        await this.checkExternalPressureEscalations(incidentId, orgId, incidentData, externalRule, now);
      }

    } catch (error) {
      console.error('Error evaluating incident escalations:', error);
    }
  }

  /**
   * Check severity-based escalations
   */
  async checkSeverityEscalations(incidentId, orgId, incidentData, rule, now) {
    const incidentCreated = incidentData.createdAt?.toDate() || incidentData.occurredAt?.toDate();
    if (!incidentCreated) return;

    const responseTimeMinutes = (now - incidentCreated) / (1000 * 60);

    for (const trigger of rule.triggers) {
      if (trigger.condition !== 'severity_response_time') continue;
      if (incidentData.severity !== trigger.severity) continue;

      if (responseTimeMinutes >= trigger.maxResponseMinutes) {
        await this.triggerEscalation({
          incidentId,
          orgId,
          ruleId: rule.id,
          triggerType: trigger.condition,
          escalationLevel: trigger.escalationLevel,
          escalateTo: trigger.escalateTo,
          reason: `${trigger.severity} severity incident exceeded ${trigger.maxResponseMinutes} minute response time`,
          incidentDetails: {
            severity: incidentData.severity,
            status: incidentData.status,
            responseTime: Math.round(responseTimeMinutes)
          },
          severity: 'critical',
          requiresImmediate: trigger.severity === 'Extreme'
        });
      }
    }
  }

  /**
   * Check external pressure escalations
   */
  async checkExternalPressureEscalations(incidentId, orgId, incidentData, rule, now) {
    const incidentCreated = incidentData.createdAt?.toDate() || incidentData.occurredAt?.toDate();
    if (!incidentCreated) return;

    const durationHours = (now - incidentCreated) / (1000 * 60 * 60);

    for (const trigger of rule.triggers) {
      if (trigger.condition === 'incident_duration') {
        if (durationHours >= trigger.durationHours && 
            trigger.severity.includes(incidentData.severity)) {
          
          await this.triggerEscalation({
            incidentId,
            orgId,
            ruleId: rule.id,
            triggerType: trigger.condition,
            escalationLevel: trigger.escalationLevel,
            escalateTo: trigger.escalateTo,
            reason: `${incidentData.severity} severity incident running for ${Math.round(durationHours)} hours`,
            incidentDetails: {
              severity: incidentData.severity,
              duration: Math.round(durationHours),
              status: incidentData.status
            },
            severity: 'high',
            requiresImmediate: trigger.escalationLevel >= 2
          });
        }
      }
    }
  }

  /**
   * Trigger an escalation
   */
  async triggerEscalation(escalationData) {
    try {
      const escalationKey = `${escalationData.ruleId}_${escalationData.triggerType}_${escalationData.incidentId}`;
      
      // Check if this escalation was already triggered recently (prevent spam)
      const existingEscalation = this.activeEscalations.get(escalationKey);
      if (existingEscalation) {
        const timeSinceLastEscalation = Date.now() - existingEscalation.timestamp;
        const cooldownPeriod = escalationData.escalationLevel * 10 * 60 * 1000; // 10 minutes per level
        
        if (timeSinceLastEscalation < cooldownPeriod) {
          console.log(`🔇 Escalation ${escalationKey} in cooldown period`);
          return;
        }
      }

      // Create escalation record
      const escalation = {
        ...escalationData,
        id: escalationKey,
        timestamp: Date.now(),
        createdAt: serverTimestamp(),
        status: 'active',
        notificationsSent: [],
        acknowledged: false
      };

      try {
        // Store in Firestore
        const escalationsRef = collection(this.db, 'c3_escalations');
        const escalationDoc = await addDoc(escalationsRef, escalation);
        
        // Cache locally
        this.activeEscalations.set(escalationKey, {
          ...escalation,
          firestoreId: escalationDoc.id
        });

        // Send notifications
        await this.sendEscalationNotifications(escalation);

        // Log the escalation
        await logActivity({
          type: ACTIVITY_TYPES.ESCALATION_TRIGGERED || 'escalation_triggered',
          userId: 'system',
          userEmail: 'system@automation',
          organisationId: escalationData.orgId,
          details: `Escalation triggered: ${escalationData.reason}`,
          metadata: {
            escalationId: escalationDoc.id,
            incidentId: escalationData.incidentId,
            ruleId: escalationData.ruleId,
            escalationLevel: escalationData.escalationLevel,
            escalateTo: escalationData.escalateTo
          }
        });

        console.log(`🚨 Escalation triggered: ${escalationData.reason}`);
        console.log(`📋 Escalation details:`, {
          level: escalationData.escalationLevel,
          severity: escalationData.severity,
          escalateTo: escalationData.escalateTo,
          requiresImmediate: escalationData.requiresImmediate
        });

      } catch (firestoreError) {
        console.warn('⚠️ Could not save escalation to Firestore (permissions issue):', firestoreError.message);
        
        // Cache locally even if Firestore fails
        this.activeEscalations.set(escalationKey, escalation);
        
        // Log escalation locally
        console.log(`🚨 Escalation triggered (local only): ${escalationData.reason}`);
        
        // Don't throw the error to prevent infinite loops
        return;
      }

    } catch (error) {
      console.error('Error triggering escalation:', error);
      // Don't throw to prevent infinite loops - just log and continue
    }
  }

  /**
   * Send escalation notifications
   */
  async sendEscalationNotifications(escalation) {
    try {
      // This would integrate with notification services
      // For now, we'll create notification records in Firestore
      
      const notifications = [];
      
      for (const recipientRole of escalation.escalateTo) {
        const notification = {
          type: 'escalation',
          severity: escalation.severity,
          recipientRole: recipientRole,
          incidentId: escalation.incidentId,
          escalationId: escalation.firestoreId,
          title: `🚨 Escalation: ${escalation.reason}`,
          message: this.formatEscalationMessage(escalation),
          requiresImmediate: escalation.requiresImmediate,
          createdAt: serverTimestamp(),
          read: false,
          acknowledged: false
        };
        
        try {
          const notificationsRef = collection(this.db, 'c3_notifications');
          const notificationDoc = await addDoc(notificationsRef, notification);
          
          notifications.push({
            id: notificationDoc.id,
            recipientRole: recipientRole
          });
        } catch (notificationError) {
          console.warn('⚠️ Could not save notification to Firestore (permissions issue):', notificationError.message);
          // Continue with other notifications even if one fails
          notifications.push({
            id: `local-${Date.now()}-${recipientRole}`,
            recipientRole: recipientRole,
            local: true
          });
        }
      }

      // Update escalation with sent notifications (only if escalation was saved to Firestore)
      if (escalation.firestoreId && notifications.some(n => !n.local)) {
        try {
          const escalationRef = doc(this.db, 'c3_escalations', escalation.firestoreId);
          await updateDoc(escalationRef, {
            notificationsSent: notifications,
            lastNotificationSent: serverTimestamp()
          });
        } catch (updateError) {
          console.warn('⚠️ Could not update escalation with notification status:', updateError.message);
          // Not critical - continue without throwing
        }
      }

      console.log(`📱 Sent ${notifications.length} escalation notifications`);

    } catch (error) {
      console.error('Error sending escalation notifications:', error);
      // Don't throw to prevent loops - notifications are not critical for escalation functionality
    }
  }

  /**
   * Format escalation message
   */
  formatEscalationMessage(escalation) {
    let message = `Incident escalation triggered: ${escalation.reason}\n\n`;
    
    message += `📊 Escalation Level: ${escalation.escalationLevel}\n`;
    message += `⚠️ Severity: ${escalation.severity}\n`;
    message += `🕐 Time: ${new Date().toLocaleString()}\n\n`;

    if (escalation.affectedTasks) {
      message += `📋 Affected Tasks:\n`;
      escalation.affectedTasks.forEach(task => {
        message += `• ${task.name}\n`;
      });
      message += `\n`;
    }

    if (escalation.affectedUsers) {
      message += `👥 Affected Users:\n`;
      escalation.affectedUsers.forEach(user => {
        message += `• ${user.owner}: ${user.taskCount || user.criticalTasks} tasks\n`;
      });
      message += `\n`;
    }

    if (escalation.incidentDetails) {
      message += `🔍 Incident Details:\n`;
      Object.entries(escalation.incidentDetails).forEach(([key, value]) => {
        message += `• ${key}: ${value}\n`;
      });
      message += `\n`;
    }

    if (escalation.requiresImmediate) {
      message += `🚨 IMMEDIATE ATTENTION REQUIRED\n`;
    }

    return message;
  }

  /**
   * Perform periodic escalation checks
   */
  async performPeriodicEscalationCheck(incidentId, orgId) {
    // Don't perform checks if service is disabled
    if (this.isDisabled) {
      console.log('🔇 Periodic escalation check skipped - service disabled due to permissions');
      return;
    }

    try {
      console.log(`🔍 Performing periodic escalation check for incident: ${incidentId}`);
      
      // Get current incident and task data
      const incidentRef = doc(this.db, 'c3_incidents', incidentId);
      const incidentDoc = await getDoc(incidentRef);
      
      if (!incidentDoc.exists()) return;
      
      const tasksRef = collection(this.db, 'c3_tasks');
      const q = query(
        tasksRef,
        where('incidentId', '==', incidentId),
        where('orgId', '==', orgId)
      );
      
      const tasksSnapshot = await getDocs(q);
      
      // Re-evaluate all escalations
      await this.evaluateTaskEscalations(incidentId, orgId, tasksSnapshot.docs);
      await this.evaluateIncidentEscalations(incidentId, orgId, incidentDoc.data());
      
    } catch (error) {
      console.error('Error in periodic escalation check:', error);
      
      // Disable service if permission error occurs
      if (error.code === 'permission-denied' || error.message.includes('permission')) {
        console.warn('⚠️ Disabling escalation service due to permission error in periodic check');
        this.isDisabled = true;
        this.permissionError = error;
        this.cleanup(incidentId); // Clean up listeners to stop the loop
      }
    }
  }

  /**
   * Get escalation status for an incident
   */
  async getEscalationStatus(incidentId, orgId) {
    // Return early if service is disabled due to permissions
    if (this.isDisabled) {
      return {
        totalEscalations: 0,
        activeEscalations: 0,
        recentEscalations: [],
        escalationLevels: { level1: 0, level2: 0, level3: 0 },
        monitoringActive: false,
        disabled: true,
        permissionError: this.permissionError?.message || 'Insufficient permissions',
        lastCheck: new Date().toISOString()
      };
    }

    try {
      const escalationsRef = collection(this.db, 'c3_escalations');
      const q = query(
        escalationsRef,
        where('incidentId', '==', incidentId),
        where('orgId', '==', orgId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const escalations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const activeEscalations = escalations.filter(e => e.status === 'active');
      const recentEscalations = escalations.slice(0, 10);

      return {
        totalEscalations: escalations.length,
        activeEscalations: activeEscalations.length,
        recentEscalations: recentEscalations,
        escalationLevels: {
          level1: escalations.filter(e => e.escalationLevel === 1).length,
          level2: escalations.filter(e => e.escalationLevel === 2).length,
          level3: escalations.filter(e => e.escalationLevel === 3).length
        },
        monitoringActive: this.escalationListeners.has(`incident_${incidentId}`),
        disabled: false,
        lastCheck: new Date().toISOString()
      };

    } catch (error) {
      console.error('Error getting escalation status:', error);
      
      // Disable service if permission error occurs
      if (error.code === 'permission-denied' || error.message.includes('permission')) {
        this.isDisabled = true;
        this.permissionError = error;
        this.cleanup(incidentId); // Clean up any existing listeners
      }
      
      return {
        totalEscalations: 0,
        activeEscalations: 0,
        recentEscalations: [],
        escalationLevels: { level1: 0, level2: 0, level3: 0 },
        monitoringActive: false,
        disabled: this.isDisabled,
        permissionError: error.message,
        error: error.message
      };
    }
  }

  /**
   * Acknowledge an escalation
   */
  async acknowledgeEscalation(escalationId, userId, userEmail) {
    try {
      const escalationRef = doc(this.db, 'c3_escalations', escalationId);
      
      await updateDoc(escalationRef, {
        acknowledged: true,
        acknowledgedBy: userId,
        acknowledgedAt: serverTimestamp(),
        status: 'acknowledged'
      });

      console.log(`✅ Escalation acknowledged: ${escalationId} by ${userEmail}`);

    } catch (error) {
      console.error('Error acknowledging escalation:', error);
    }
  }

  /**
   * Clean up escalation monitoring
   */
  cleanup(incidentId) {
    const listeners = [
      `tasks_${incidentId}`,
      `incident_${incidentId}`,
      `periodic_${incidentId}`
    ];

    listeners.forEach(key => {
      const cleanup = this.escalationListeners.get(key);
      if (cleanup) {
        cleanup();
        this.escalationListeners.delete(key);
      }
    });

    // Clean up cached escalations
    const keysToRemove = [];
    this.activeEscalations.forEach((value, key) => {
      if (key.includes(incidentId)) {
        keysToRemove.push(key);
      }
    });
    
    keysToRemove.forEach(key => this.activeEscalations.delete(key));

    console.log(`🧹 Cleaned up escalation monitoring for incident: ${incidentId}`);
  }
}

export default SmartEscalationService; 